import React from 'react';
import {styled} from '@mui/material';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import useLiveEstimatedMinutes from '@contentlab/hooks/use-live-estimated-minutes';
import {FeatureName, FlowStatus} from '@contentlab/types/project';
import {AssetProps} from '@contentlab/types/projectProps';
import {
  FEATURE_NAME_TO_METADATA_PROPERTY,
  getMetadataDisplayValue,
} from '@contentlab/utils/featureHelpers';
import translate from '@/utils/translate';

interface VideoSubtitleProps {
  asset: AssetProps;
  feature: FeatureName;
  uploadAssets: AssetProps[];
  onSubtitleActionClick: (originalAsset: AssetProps) => void;
}

interface SubtitleActionProps {
  from: number | string | undefined;
  to: number | string | undefined;
  fromAsset: AssetProps;
  feature: FeatureName;
  onSubtitleActionClick: (originalAsset: AssetProps) => void;
}

const FromAssetButton = styled('span')(() => ({
  cursor: 'pointer',
  textDecoration: 'underline',
}));

function SubtitleAction(props: SubtitleActionProps) {
  const {from, to, fromAsset, feature, onSubtitleActionClick} = props;
  const onClick = (event: React.MouseEvent) => {
    onSubtitleActionClick(fromAsset);
    event.stopPropagation();
  };
  switch (feature) {
    case FeatureName.AI_DUBBING: {
      const text = translate('language dub', {
        from: from || 'unknown',
        to: to || 'unknown',
      });
      const action = (
        <FromAssetButton onClick={onClick}>
          {translate('see original')}
        </FromAssetButton>
      );
      return (
        <>
          {text}, {action}
        </>
      );
    }
    case FeatureName.REFORMATTING: {
      const text = translate('resized from', {
        from: from || 'unknown',
        to: to || 'unknown',
      });
      const action = (
        <FromAssetButton onClick={onClick}>{from}</FromAssetButton>
      );
      return (
        <>
          {text} {action}
        </>
      );
    }
    case FeatureName.REPHRASING: {
      const text = translate('Rescripted video');
      const action = (
        <FromAssetButton onClick={onClick}>
          {translate('see original')}
        </FromAssetButton>
      );
      return (
        <>
          {text}, {action}
        </>
      );
    }
    case FeatureName.UPSCALE: {
      const text = translate('upscaled to', {
        from: from || 'unknown',
        to: to || 'unknown',
      });
      const action = (
        <FromAssetButton onClick={onClick}>
          {translate('see original')}
        </FromAssetButton>
      );
      return (
        <>
          {text}, {action}
        </>
      );
    }
    default:
      return null;
  }
}

function VideoSubtitle(props: VideoSubtitleProps) {
  const {asset, feature, uploadAssets, onSubtitleActionClick} = props;
  const {remainingMinutes} = useLiveEstimatedMinutes(
    asset.estimatedCompletionTime,
    asset.createdAt
  );
  if (!asset || asset.type === 'inputAsset') {
    return null;
  }
  if (asset.status === FlowStatus.IN_PROGRESS) {
    return (
      <Body
        size="lg"
        color={designSystemToken('semantic.fg.inverse-secondary')}
      >
        {typeof remainingMinutes === 'number'
          ? translate('Processing ETA', {count: remainingMinutes})
          : translate('Processing')}
      </Body>
    );
  }
  const originalAsset = uploadAssets.find(
    (uploadAsset) => uploadAsset.id === asset.pairedAssetId
  );

  if (!originalAsset) {
    return null;
  }

  const from = getMetadataDisplayValue(
    originalAsset.metadata,
    FEATURE_NAME_TO_METADATA_PROPERTY[feature],
    {
      status: originalAsset.status,
      userInput: originalAsset.userInput,
      assetType: originalAsset.type,
    }
  );
  const to = getMetadataDisplayValue(
    asset.metadata,
    FEATURE_NAME_TO_METADATA_PROPERTY[feature],
    {status: asset.status, userInput: asset.userInput, assetType: asset.type}
  );
  const subtitleAction = (
    <SubtitleAction
      from={from}
      to={to}
      fromAsset={originalAsset}
      feature={feature}
      onSubtitleActionClick={onSubtitleActionClick}
    />
  );

  return (
    <Body size="sm" color={designSystemToken('semantic.fg.inverse-secondary')}>
      {subtitleAction}
    </Body>
  );
}

export default VideoSubtitle;
